'use client'
import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BsChatDots,
  BsPerson,
  BsEnvelope,
  BsBoxArrowRight,
  BsCreditCard
} from 'react-icons/bs';
import { HiOutlineUserCircle } from 'react-icons/hi';
import { useLogoutMutation } from '@/graphql/auth';
import { client } from '@/graphql/lib/apollo';
import { toast } from 'react-toastify';
import { useUser } from '@/services/contexts/UserContext';
import { useLocale, useTranslations } from 'next-intl';
import UnreadBadge from '../atoms/UnreadBadge';

const ProfileDropdown = ({ user, unreadCount }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const [logout] = useLogoutMutation();
  const { setUser } = useUser();
  const t = useTranslations("dropdown")
  const locale = useLocale();
  const isRtl = locale === 'ar';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      const { data } = await logout();
      if (data?.logout.success) {
        await client.clearStore();
        setUser(null);
        toast.success("تم تسجيل الخروج")
        router.push("/sign-in");
      }
    } catch {
      toast.error("فشل تسجيل الخروج")
    }
  };

  return (
    <div className="relative flex items-center" ref={dropdownRef}>
      <div className="flex items-center space-x-4">
        <Link
          href="/profile/chats"
          className="relative p-1.5 rounded-full transition-colors"
          aria-label="Messages"
        >
          <BsChatDots className="text-xl text-[var(--color-gold-bright)] hover:text-[var(--color-olive-dark)] duration-500" />
          <UnreadBadge count={unreadCount} />
        </Link>

        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center focus:outline-none rounded-full"
          aria-expanded={isOpen}
          aria-label="Profile menu"
        >
          {user?.avatarUrl ? (
            <Image
              quality={80}
              unoptimized={true}
              width={120}
              height={120}
              src={`${user?.avatarUrl}`}
              loading="lazy"
              alt="Profile Avatar"
              className="w-9 h-9 rounded-full object-cover border-2 border-[var(--color-gold-bright)] cursor-pointer hover:opacity-90 transition-opacity"
            />
          ) : (
            <div className="p-1.5 rounded-full">
              <HiOutlineUserCircle className="text-2xl cursor-pointer hover:text-[var(--color-olive-dark)] transition-colors duration-500 text-[var(--color-gold-bright)]" />
            </div>
          )}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{
              duration: 0.2,
              ease: "easeOut"
            }}
            className={`absolute ${isRtl ? "left-0" : "right-0"}  top-14 w-56 bg-white rounded-xl shadow-xl z-50 border border-gray-200 overflow-hidden`}
          >
            <div className="py-2">
              <Link
                href="/profile"
                className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <BsPerson className={`text-[var(--color-gold-bright)] ${isRtl ? "ml-2" : "mr-2"} text-lg flex-shrink-0`} />
                <span className="font-medium">
                  {t("profile")}
                </span>
                {user?.role && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {t(`roles.${user.role}`)}
                  </span>
                )}
              </Link>

              <Link
                href="/profile/payments"
                className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <BsCreditCard className={`text-[var(--color-gold-bright)] ${isRtl ? "ml-2" : "mr-2"} text-lg flex-shrink-0`} />
                <span className="font-medium">
                  {t("payments")}
                </span>
              </Link>


              <Link
                href="/profile/chats"
                className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <BsEnvelope className={`text-[var(--color-gold-bright)]  ${isRtl ? "ml-2" : "mr-2"} text-lg flex-shrink-0`} />
                <span className="font-medium">
                  {t("messages")}
                </span>
              </Link>

              <button
                onClick={handleLogout}
                className="flex items-center cursor-pointer w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <BsBoxArrowRight className={`text-[var(--color-gold-bright)] ${isRtl ? "ml-2" : "mr-2"} text-lg flex-shrink-0`} />
                <span className="font-medium">
                  {t("logout")}
                </span>
              </button>
            </div>

            <div className="border-t border-gray-100 bg-gray-50 px-4 py-2.5">
              <p className="text-xs font-medium text-gray-500 truncate">
                {user?.email || 'user@example.com'}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfileDropdown;